
export interface DeepgramCallbacks {
  onInterimResult?: (transcript: string) => void;
  onFinalResult?: (transcript: string) => void;
  onError?: (error: string) => void;
  onEnd?: () => void;
}

export interface DeepgramConfig {
  existingMediaStream?: MediaStream;
}

/**
 * Streams microphone audio to Deepgram realtime websocket and maps results
 * to the same callback interface used by SpeechRecognitionService.
 */
class DeepgramTranscriptionService {
  private ws: WebSocket | null = null;
  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private isRunning = false;
  private manuallyStopping = false;
  private restartTimer: number | null = null;
  private callbacks: DeepgramCallbacks;
  private readonly url: string;
  private readonly apiKey: string | undefined;
  private timesliceMs = 100; // Increased for better stability and voice detection
  // Utterance aggregation to merge close-together finals into one
  private utteranceBuffer = "";
  private flushTimer: number | null = null;
  private lastActivityAt = 0;
  private flushSilenceMs = 100; // Increased for more reliable final results
  private config: DeepgramConfig;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private volumeCheckInterval: number | null = null;

  constructor(callbacks: DeepgramCallbacks = {}, config: DeepgramConfig = {}) {
    this.callbacks = callbacks;
    this.config = config;
    // Default URL and key from env
    // Reduced endpointing for faster response - 500ms instead of 1000ms
    this.url = "wss://api.deepgram.com/v1/listen?model=nova-3&language=fr-CA&smart_format=true&interim_results=true&endpointing=200";
    this.apiKey = (import.meta as any).env?.VITE_DEEPGRAM_API_KEY;
    
    console.log("🔑 Deepgram API Key present:", !!this.apiKey);
    console.log("🔑 Deepgram API Key length:", this.apiKey?.length || 0);
    console.log("🌐 Deepgram URL:", this.url);
  }

  public start(): boolean {
    if (this.isRunning) {
      return true;
    }

    if (!("MediaRecorder" in window)) {
      this.callbacks.onError?.("MediaRecorder not supported in this browser.");
      return false;
    }

    if (!this.apiKey) {
      this.callbacks.onError?.("Missing VITE_DEEPGRAM_API_KEY. Set it in your environment.");
      return false;
    }

    this.manuallyStopping = false;
    this.connectAndStart().catch((err) => {
      console.error("Failed to start Deepgram transcription:", err);
      this.callbacks.onError?.("Failed to start Deepgram transcription");
    });
    return true;
  }
  private readonly gracefulCloseDelayMs = 5000;
  public stop(): void {
    if (this.manuallyStopping) return;

    console.log("🛑 Deepgram stop requested — starting graceful shutdown");
    this.manuallyStopping = true;
    this.isRunning = false;

    // Cancel any restart attempts
    if (this.restartTimer) {
      window.clearTimeout(this.restartTimer);
      this.restartTimer = null;
    }

    // 1️⃣ Stop recording immediately (no more audio chunks)
    try {
      if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
        console.log("🎤 Stopping MediaRecorder (graceful)");
        this.mediaRecorder.stop();
      }
    } catch (e) {
      console.warn("⚠️ Failed to stop MediaRecorder:", e);
    }

    // 2️⃣ Schedule delayed socket close
    window.setTimeout(() => {
      console.log("⏳ Grace period ended — closing Deepgram socket");

      // Force-flush any remaining buffered transcript
      if (this.flushTimer) {
        window.clearTimeout(this.flushTimer);
        this.flushTimer = null;
      }

      if (this.utteranceBuffer.trim()) {
        const finalText = this.utteranceBuffer.trim();
        this.utteranceBuffer = "";
        console.log("🧾 Emitting final buffered transcript:", finalText);
        this.callbacks.onFinalResult?.(finalText);
      }

      // Close WebSocket cleanly
      try {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.close(1000, "Graceful client stop");
        }
      } catch (e) {
        console.warn("⚠️ Failed to close WebSocket:", e);
      }

      // Cleanup MediaStream ONLY if owned
      if (this.mediaStream && !this.config.existingMediaStream) {
        try {
          this.mediaStream.getTracks().forEach(t => t.stop());
        } catch {}
      }

      this.mediaStream = null;
      this.mediaRecorder = null;
      this.ws = null;

      console.log("✅ Deepgram fully stopped");
      this.callbacks.onEnd?.();

    }, this.gracefulCloseDelayMs);
  }


  public isActive(): boolean {
    return this.isRunning && this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  public isHealthy(): boolean {
    return this.isRunning && 
           this.mediaRecorder && 
           this.mediaRecorder.state === 'recording' &&
           this.ws && 
           this.ws.readyState === WebSocket.OPEN;
  }

  public getStatus(): { isRunning: boolean; wsState: number | null; recorderState: string | null } {
    return {
      isRunning: this.isRunning,
      wsState: this.ws?.readyState || null,
      recorderState: this.mediaRecorder?.state || null
    };
  }

  public async testMicrophone(): Promise<{ success: boolean; message: string; details?: any }> {
    try {
      console.log("🧪 Testing microphone access...");
      
      // Test 1: Check if MediaRecorder is supported
      if (!('MediaRecorder' in window)) {
        return {
          success: false,
          message: "MediaRecorder not supported in this browser"
        };
      }
      
      // Test 2: Check microphone permissions
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (permissionStatus.state === 'denied') {
          return {
            success: false,
            message: "Microphone permission denied. Please allow microphone access in browser settings."
          };
        }
      } catch (e) {
        console.warn("Could not check permissions:", e);
      }
      
      // Test 3: Try to get microphone stream
      const testStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      const tracks = testStream.getTracks();
      const audioTrack = tracks.find(t => t.kind === 'audio');
      
      if (!audioTrack) {
        testStream.getTracks().forEach(t => t.stop());
        return {
          success: false,
          message: "No audio track found in media stream"
        };
      }
      
      // Test 4: Check if track is enabled and not muted
      const trackDetails = {
        enabled: audioTrack.enabled,
        readyState: audioTrack.readyState,
        muted: audioTrack.muted,
        label: audioTrack.label
      };
      
      console.log("🎤 Microphone track details:", trackDetails);
      
      // Test 5: Try to create MediaRecorder
      let mimeType = "audio/webm;codecs=opus";
      if (!(window as any).MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "audio/webm";
        if (!(window as any).MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "audio/mp4";
        }
      }
      
      const testRecorder = new MediaRecorder(testStream, { mimeType });
      
      // Clean up test resources
      testRecorder.stop();
      testStream.getTracks().forEach(t => t.stop());
      
      return {
        success: true,
        message: "Microphone test passed! All checks successful.",
        details: {
          ...trackDetails,
          mimeType,
          browserSupport: {
            mediaRecorder: true,
            getUserMedia: true
          }
        }
      };
      
    } catch (error: any) {
      console.error("❌ Microphone test failed:", error);
      return {
        success: false,
        message: `Microphone test failed: ${error.message || error}`,
        details: { error: error.toString() }
      };
    }
  }

  public cleanup(): void {
    // Clean up audio level monitoring
    if (this.volumeCheckInterval) {
      window.clearInterval(this.volumeCheckInterval);
      this.volumeCheckInterval = null;
    }
    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    this.stop();
  }

  private setupAudioLevelMonitoring(): void {
    try {
      // Create audio context to monitor microphone levels
      const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContextClass();
      
      // Create analyser node
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      
      // Connect microphone stream to analyser
      const source = this.audioContext.createMediaStreamSource(this.mediaStream!);
      source.connect(this.analyser);
      
      const bufferLength = this.analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      // Check audio levels every second
      this.volumeCheckInterval = window.setInterval(() => {
        if (!this.analyser) return;
        
        this.analyser.getByteFrequencyData(dataArray);
        
        // Calculate average volume
        const sum = dataArray.reduce((a, b) => a + b, 0);
        const average = sum / bufferLength;
        
        // Calculate peak volume for better sensitivity indication
        const peak = Math.max(...dataArray);
        
        // Log volume level (0-255 scale) - using peak for better indication
        if (average > 5) {
          // console.log("🎤 Microphone audio level:", Math.round(average), "/ 255 (peak:", peak, ")");
        } else {
          // console.log("🎤 Microphone audio level:", Math.round(average), "/ 255 (peak:", peak, ") - NO SOUND DETECTED ⚠️");
        }
      }, 2000); // Check every 2 seconds
      
      console.log("✅ Audio level monitoring enabled");
    } catch (error) {
      console.warn("⚠️ Could not set up audio level monitoring:", error);
    }
  }

  private async connectAndStart(): Promise<void> {
    try {
      // Check microphone permissions first
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        console.log("🎤 Microphone permission status:", permissionStatus.state);
        
        if (permissionStatus.state === 'denied') {
          console.error("❌ Microphone permission denied by user");
          this.callbacks.onError?.("Microphone permission denied. Please allow microphone access in your browser settings.");
          return;
        }
      } catch (e) {
        console.warn("⚠️ Could not check microphone permissions:", e);
      }
      
      // Use existing MediaStream if provided, otherwise create new one
      if (this.config.existingMediaStream) {
        this.mediaStream = this.config.existingMediaStream;
        console.log("🎤 Deepgram: Using existing MediaStream from AudioService");
        console.log("🎤 Deepgram: MediaStream ID:", this.mediaStream.id);
        console.log("🎤 Deepgram: MediaStream active:", this.mediaStream.active);
        console.log("🎤 Deepgram: MediaStream tracks:", this.mediaStream.getTracks().map(t => ({
          kind: t.kind,
          enabled: t.enabled,
          readyState: t.readyState,
          muted: t.muted,
          id: t.id,
          label: t.label
        })));
      } else {
        // Get mic with mobile-optimized constraints and boosted sensitivity
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            // Mobile-specific optimizations
            latency: 0.01,
            volume: 1.0
          } as MediaTrackConstraints,
        });
        console.log("🎤 Deepgram: New MediaStream acquired successfully");
        console.log("🎤 Deepgram: MediaStream tracks:", this.mediaStream.getTracks().map(t => ({
          kind: t.kind,
          enabled: t.enabled,
          readyState: t.readyState,
          muted: t.muted
        })));
      }
      
      // Keep microphone stream alive and verify it's working
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => {
          track.enabled = true;
          console.log("🎤 Deepgram: Track enabled:", {
            kind: track.kind,
            enabled: track.enabled,
            readyState: track.readyState,
            muted: track.muted,
            label: track.label
          });
        });
        
        // Set up audio level monitoring to verify microphone is picking up sound
        this.setupAudioLevelMonitoring();
      }

      // Pick a supported mimeType
      let mimeType = "audio/webm;codecs=opus";
      if (!(window as any).MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "audio/webm";
        if (!(window as any).MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "audio/mp4"; // last resort
        }
      }

      // Create socket
      console.log("🔌 Creating Deepgram WebSocket connection...");
      this.ws = new WebSocket(this.url, ["token", this.apiKey!]);

      this.ws.onopen = () => {
        this.isRunning = true;
        console.log("🎤 Deepgram WebSocket connected - voice detection active");
        console.log("🔌 WebSocket readyState:", this.ws?.readyState);
        
        // Ensure mediaStream is available before creating MediaRecorder
        if (!this.mediaStream) {
          console.error("❌ MediaStream not available when creating MediaRecorder");
          this.callbacks.onError?.("MediaStream not available");
          this.scheduleRestart();
          return;
        }
        
        try {
          this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType });
        } catch (err) {
          console.error("Failed to create MediaRecorder:", err);
          this.callbacks.onError?.("Failed to create MediaRecorder");
          this.scheduleRestart();
          return;
        }

      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
          console.log("⚠️ MediaRecorder data available but WebSocket not ready:", this.ws?.readyState);
          return;
        }
        if (event.data && event.data.size > 0) {
          // console.log("📤 Sending audio data to Deepgram:", event.data.size, "bytes");
          this.ws.send(event.data);
        } else {
          console.warn("⚠️ MediaRecorder data available but no data or empty data - microphone may not be working");
        }
      };

      this.mediaRecorder.onerror = (e: any) => {
        console.error("❌ MediaRecorder error:", e?.error || e);
        console.error("❌ MediaRecorder state:", this.mediaRecorder?.state);
        console.error("❌ WebSocket state:", this.ws?.readyState);
        this.callbacks.onError?.("MediaRecorder error");
        this.scheduleRestart();
      };

      try {
        this.mediaRecorder.start(this.timesliceMs);
        console.log("🎤 Deepgram MediaRecorder started - listening for speech");
        console.log("🎤 MediaRecorder state:", this.mediaRecorder.state);
        console.log("🎤 MediaRecorder mimeType:", this.mediaRecorder.mimeType);
        console.log("🎤 Timeslice:", this.timesliceMs);
        
        // Add a test to see if we get any data
        setTimeout(() => {
          console.log("🔍 MediaRecorder state after 1s:", this.mediaRecorder?.state);
          console.log("🔍 WebSocket state after 1s:", this.ws?.readyState);
        }, 1000);
      } catch (err) {
        console.error("❌ Failed to start MediaRecorder:", err);
        console.error("❌ MediaStream tracks:", this.mediaStream?.getTracks().map(t => ({
          kind: t.kind,
          enabled: t.enabled,
          readyState: t.readyState,
          muted: t.muted
        })));
        this.callbacks.onError?.("Failed to start MediaRecorder");
        this.scheduleRestart();
      }
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        
        // Deepgram realtime message shape
        const alt = data?.channel?.alternatives?.[0];
        if (alt && typeof alt.transcript === "string") {
          const transcript: string = alt.transcript;
          const isFinal: boolean = !!data?.is_final;
          if (transcript) {
            this.lastActivityAt = Date.now();
            if (isFinal) {
              // Append to buffer and schedule flush; do not emit immediately
              const finalText = transcript.trim();
              if (finalText) {
                const cleaned = this.removeConsecutiveDuplicates(finalText);
                console.log("✅ Deepgram FINAL (immediate):", finalText);
                this.callbacks.onFinalResult?.(cleaned);
              }
              this.scheduleFlush();
            } else {
              console.log("🎤 Deepgram interim speech detected:", transcript);
              this.callbacks.onInterimResult?.(transcript);
              // Keep the flush timer armed, but don't emit yet
              this.scheduleFlush();
            }
          }
        } else {
          console.log("📥 Deepgram message with no transcript:", data);
        }
      } catch (err) {
        // Non-JSON messages are possible; log and ignore
        console.log("📥 Deepgram non-JSON message:", event.data);
      }
    };

    this.ws.onerror = (err) => {
      console.error("❌ Deepgram WS error:", err);
      console.error("❌ WebSocket readyState:", this.ws?.readyState);
      console.error("❌ MediaRecorder state:", this.mediaRecorder?.state);
      this.callbacks.onError?.("Deepgram connection error");
      this.scheduleRestart();
    };

    this.ws.onclose = (event) => {
      console.log("🔌 Deepgram WebSocket closed:", {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        manuallyStopping: this.manuallyStopping
      });
      this.isRunning = false;
      if (this.flushTimer) { window.clearTimeout(this.flushTimer); this.flushTimer = null; }
      this.utteranceBuffer = "";
      if (!this.manuallyStopping) {
        console.log("🔄 WebSocket closed unexpectedly - scheduling restart");
        this.scheduleRestart();
      }
    };
    } catch (error) {
      console.error("❌ Failed to acquire MediaStream:", error);
      this.callbacks.onError?.("Failed to acquire microphone access");
      this.scheduleRestart();
    }
  }

  private scheduleFlush() {
    if (this.flushTimer) {
      window.clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    // Debounce; if no activity for flushSilenceMs, emit a single final result
    this.flushTimer = window.setTimeout(() => {
      const now = Date.now();
      const idle = now - this.lastActivityAt;
      if (idle >= this.flushSilenceMs && this.utteranceBuffer) {
        const finalText = this.utteranceBuffer.trim();
        this.utteranceBuffer = "";
        this.callbacks.onFinalResult?.(finalText);
      } else {
        // Not idle long enough; re-arm quickly to check again
        this.scheduleFlush();
      }
    }, this.flushSilenceMs);
  }
  
    private scheduleRestart() {
      if (this.manuallyStopping) return;
      if (this.restartTimer) {
        window.clearTimeout(this.restartTimer);
        this.restartTimer = null;
      }
      // Try a quick restart with shorter delay for better responsiveness
      this.restartTimer = window.setTimeout(() => {
        if (!this.manuallyStopping) {
          console.log("🔄 Attempting to restart Deepgram voice detection...");
          this.connectAndStart().catch((err) => {
            console.error("Deepgram reconnect failed:", err);
            this.callbacks.onError?.("Deepgram reconnect failed");
            // Try again after a longer delay if first attempt fails
            this.scheduleRestart();
          });
        }
      }, 500); // Reduced from 800ms to 500ms for faster recovery
    }
    /**
   * Remove consecutive duplicate words from ASR output
   * Example: "pour pour pour" → "pour"
   * Example: "les les services" → "les services"
   */
  private removeConsecutiveDuplicates(text: string): string {
    const words = text.split(/\s+/);
    const result: string[] = [];
  
    for (let i = 0; i < words.length; i++) {
      if (i === 0 || words[i] !== words[i - 1]) {
        result.push(words[i]);
      } else {
        console.log("🧹 Removed duplicate word:", words[i]);
      }
    }
    return result.join(" ");
  }
}

export default DeepgramTranscriptionService; 